import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NavigateFunction } from 'react-router-dom';

import { getUrlParamsFetch, notFoundIdHandler } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import { TipoImpuesto, TipoImpuestoPagingRes } from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';

const { get, post, put } = erpApi();

///* tanSctack query
export const useFetchTipoImpuestos = (params?: GetTipoImpuestosParams) => {
  return useQuery({
    queryKey: ['tipoImpuestos', ...Object.values(params ?? {})],
    queryFn: async () => {
      return getTipoImpuestos(params);
    },
  });
};

export const useGetTipoImpuesto = (id: string) => {
  return useQuery({
    queryKey: ['tipoImpuesto', id],
    queryFn: async () => {
      return getTipoImpuesto(id);
    },
  });
};

export const useCreateTipoImpuesto = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createTipoImpuesto,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tipoImpuestos'] });
    },
    onSettled: () => {
      navigate(returnUrl);
    },
  });
};

export const useUpdateTipoImpuesto = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateTipoImpuesto,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tipoImpuestos'] });
    },
    onSettled: () => {
      navigate(returnUrl);
    },
  });
};

///* axios
export type GetTipoImpuestosParams = {
  page?: number;
  page_size?: number;

  nombre?: string;
  codigo?: string;
};

export type CreateTipoImpuestoParams = Omit<TipoImpuesto, 'id_tipo_impuesto'>;
export type UpdateTipoImpuestoParams = {
  id: string;
  data: CreateTipoImpuestoParams;
};

export const getTipoImpuestos = async (params?: GetTipoImpuestosParams) => {
  const queryParams = getUrlParamsFetch(params || {});

  return get<TipoImpuestoPagingRes>(
    `${urlBackend}api/v1/tipos-impuesto/?${queryParams}`,
    true
  );
};

export const getTipoImpuesto = async (id: string) => {
  const res = await get<TipoImpuesto>(
    `${urlBackend}api/v1/tipos-impuesto/${id}/`,
    true
  );

  notFoundIdHandler(res);
  return res;
};

export const createTipoImpuesto = async (data: CreateTipoImpuestoParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await post<TipoImpuesto>(
    `${urlBackend}api/v1/tipos-impuesto/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};

export const updateTipoImpuesto = async ({
  id,
  data,
}: UpdateTipoImpuestoParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await put<TipoImpuesto>(
    `${urlBackend}api/v1/tipos-impuesto/${id}/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};
